import { FC, PointerEvent, useCallback, useEffect, useRef } from 'react';

import { Box } from '@bonusly/design-system';

import { renderStrokeToCtx } from './stroke-utils';
import type { DoodleStroke, DoodleTool } from './use-doodle';

interface DoodleCanvasProps {
	tool: DoodleTool;
	strokes: DoodleStroke[];
	currentStroke: DoodleStroke | null;
	onStartStroke: (point: number[]) => void;
	onAddPoint: (point: number[]) => void;
	onEndStroke: () => void;
	onFill: (point: number[]) => void;
}

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 500;

export const DoodleCanvas: FC<DoodleCanvasProps> = ({ tool, strokes, currentStroke, onStartStroke, onAddPoint, onEndStroke, onFill }) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const isDrawing = useRef(false);
	const rafRef = useRef<number | null>(null);
	const cacheCanvasRef = useRef<HTMLCanvasElement | null>(null);
	const cachedStrokesCountRef = useRef(0);

	const getCanvasPoint = useCallback((e: PointerEvent): number[] => {
		const canvas = canvasRef.current;
		if (!canvas) return [0, 0];

		const rect = canvas.getBoundingClientRect();
		const scaleX = CANVAS_WIDTH / rect.width;
		const scaleY = CANVAS_HEIGHT / rect.height;

		const x = (e.clientX - rect.left) * scaleX;
		const y = (e.clientY - rect.top) * scaleY;
		const pressure = e.pressure;

		return [x, y, pressure];
	}, []);

	const handlePointerDown = useCallback(
		(e: PointerEvent) => {
			e.preventDefault();
			if (tool === 'fill') {
				onFill(getCanvasPoint(e));
				return;
			}
			(e.target as Element).setPointerCapture(e.pointerId);
			isDrawing.current = true;
			onStartStroke(getCanvasPoint(e));
		},
		[getCanvasPoint, onStartStroke, onFill, tool]
	);

	const handlePointerMove = useCallback(
		(e: PointerEvent) => {
			if (!isDrawing.current) return;
			e.preventDefault();

			if (rafRef.current !== null) return;
			rafRef.current = requestAnimationFrame(() => {
				rafRef.current = null;
				onAddPoint(getCanvasPoint(e));
			});
		},
		[getCanvasPoint, onAddPoint]
	);

	const handlePointerUp = useCallback(
		(e: PointerEvent) => {
			e.preventDefault();
			if (!isDrawing.current) return;
			isDrawing.current = false;
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			onEndStroke();
		},
		[onEndStroke]
	);

	useEffect(() => {
		if (!cacheCanvasRef.current) {
			cacheCanvasRef.current = document.createElement('canvas');
			cacheCanvasRef.current.width = CANVAS_WIDTH;
			cacheCanvasRef.current.height = CANVAS_HEIGHT;
		}

		const cacheCanvas = cacheCanvasRef.current;
		const cacheCtx = cacheCanvas.getContext('2d');
		if (!cacheCtx) return;

		if (cachedStrokesCountRef.current > strokes.length || cachedStrokesCountRef.current === 0) {
			cacheCtx.fillStyle = '#ffffff';
			cacheCtx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
			for (const stroke of strokes) {
				renderStrokeToCtx(cacheCtx, stroke);
			}
			cachedStrokesCountRef.current = strokes.length;
		} else if (strokes.length > cachedStrokesCountRef.current) {
			for (let i = cachedStrokesCountRef.current; i < strokes.length; i++) {
				renderStrokeToCtx(cacheCtx, strokes[i]);
			}
			cachedStrokesCountRef.current = strokes.length;
		}
	}, [strokes]);

	useEffect(() => {
		const canvas = canvasRef.current;
		const cacheCanvas = cacheCanvasRef.current;
		if (!canvas || !cacheCanvas) return;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
		ctx.drawImage(cacheCanvas, 0, 0);

		if (currentStroke) {
			renderStrokeToCtx(ctx, currentStroke);
		}
	}, [strokes, currentStroke]);

	return (
		<Box
			ref={containerRef}
			position="relative"
			width="100%"
			bg="white"
			borderRadius="lg"
			overflow="hidden"
			border="1px solid"
			borderColor="border.default"
			sx={{ aspectRatio: `${CANVAS_WIDTH} / ${CANVAS_HEIGHT}`, touchAction: 'none' }}
		>
			<canvas
				ref={canvasRef}
				width={CANVAS_WIDTH}
				height={CANVAS_HEIGHT}
				style={{
					width: '100%',
					height: '100%',
					cursor: tool === 'fill' ? 'cell' : 'crosshair',
					display: 'block',
				}}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={handlePointerUp}
				onPointerLeave={handlePointerUp}
			/>
		</Box>
	);
};
